// ============================================================
// ThemeSettings.tsx — Settings → Appearance → Theme.
//   Light · Dark · Match my device · Automatic (day/night).
// Automatic = dark from UK sunset to sunrise (worked out locally —
// utils/sunTimes), or "Use fixed times instead" (default 19:00 → 07:00).
// A change applies at once (soft fade) on this device and is saved to the
// account so it follows the user to their other devices; if that save fails
// the choice still holds here and a Retry is offered.
// ============================================================
import { refusalText } from '../../utils/serverError'
import React, { useEffect, useId, useState } from 'react'
import { RadioOptionList } from '../admin/adminUi'
import { SettingsSwitch, SettingsToggleRow } from './settingsUi'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import { ErrorNote, LINK } from '../shared/ActionBox'
import { INPUT } from '../billing/billingUi'
import { nextSwitch, setAppearance, useAppearance } from '../../utils/theme'
import { saveAccountAppearance } from '../../utils/accountAppearance'
import { sunTimes, ukParts, ukTime } from '../../utils/sunTimes'
import type { Appearance, ThemeMode } from '../../types/appearance.types'

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/

// Today's UK sunset and tomorrow's sunrise, "16:53" / "07:58".
export const todaysDarkHours = (now: Date): { sunset: string; sunrise: string } => {
  const p = ukParts(now)
  const tomorrow = new Date(Date.UTC(p.y, p.m - 1, p.d + 1))
  return {
    sunset: ukTime(sunTimes(p.y, p.m, p.d).sunset),
    sunrise: ukTime(sunTimes(tomorrow.getUTCFullYear(), tomorrow.getUTCMonth() + 1, tomorrow.getUTCDate()).sunrise),
  }
}

const ThemeSettings: React.FC = () => {
  const id = useId()
  const appearance = useAppearance((s) => s.appearance)
  const theme = useAppearance((s) => s.theme)
  const [saveFailed, setSaveFailed] = useState(false)
  // The time boxes keep what's typed; only a valid pair is applied.
  const [from, setFrom] = useState(appearance.from)
  const [to, setTo] = useState(appearance.to)
  useEffect(() => { setFrom(appearance.from); setTo(appearance.to) }, [appearance.from, appearance.to])
  // Re-render each minute so "switches at …" stays right.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t) }, [])

  const [saveWhy, setSaveWhy] = useState('')
  const save = (a: Appearance): void => {
    setAppearance(a)
    setNow(new Date())
    saveAccountAppearance(a).then(() => { setSaveFailed(false); setSaveWhy('') }, (err) => { setSaveFailed(true); setSaveWhy(refusalText(err, '')) })
  }
  const choose = (mode: ThemeMode): void => save({ ...appearance, mode })

  const { sunset, sunrise } = todaysDarkHours(now)
  const options: { value: ThemeMode; label: string; help: string }[] = [
    { value: 'light', label: 'Light', help: 'Always light.' },
    { value: 'dark', label: 'Dark', help: 'Always dark.' },
    { value: 'device', label: 'Match my device', help: 'Follows the light or dark setting on this computer or phone, and changes when it does.' },
    { value: 'auto', label: 'Automatic (day/night)', help: `Dark from sunset to sunrise in the UK — today ${sunset} to ${sunrise}.` },
  ]

  const timesSame = from === to
  const timesBad = !HHMM.test(from) || !HHMM.test(to)
  const timeError = timesBad ? 'Enter both times.' : timesSame ? 'Choose two different times.' : undefined
  const setTime = (which: 'from' | 'to', v: string): void => {
    const f = which === 'from' ? v : from
    const t = which === 'to' ? v : to
    if (which === 'from') setFrom(v); else setTo(v)
    if (HHMM.test(f) && HHMM.test(t) && f !== t) save({ ...appearance, from: f, to: t })
  }

  const next = nextSwitch(appearance, now)
  const autoStatus = appearance.mode === 'auto' && next
    ? `${theme === 'dark' ? 'Dark' : 'Light'} now — switches to ${theme === 'dark' ? 'light' : 'dark'} at ${ukTime(next)}.`
    : null

  return (
    <div className="py-[13px]" data-theme-settings="">
      <div className="text-[14px] font-medium text-casevo-forest mb-[10px]" id={`${id}-label`}>Theme</div>
      <RadioOptionList label="Theme" options={options} value={appearance.mode} onChange={choose} />
      {appearance.mode === 'auto' && (
        <div className="mt-[12px] rounded-[12px] border border-casevo-border bg-casevo-surface px-[14px]" data-theme-auto="">
          <SettingsToggleRow
            title="Use fixed times instead"
            desc="Choose when dark starts and ends, instead of sunset and sunrise (UK time)."
            control={<SettingsSwitch checked={appearance.fixed} label="Use fixed times instead" onChange={() => save({ ...appearance, fixed: !appearance.fixed })} />}
          />
          {appearance.fixed && (
            <div className="grid gap-[12px] sm:grid-cols-2 pb-[4px]">
              <FormField label="Dark from" htmlFor={`${id}-from`} variant="info">
                <input id={`${id}-from`} type="time" value={from} onChange={(e) => setTime('from', e.target.value)} className={INPUT}
                  aria-invalid={timeError ? true : undefined} aria-describedby={timeError ? `${id}-time-err` : undefined} />
              </FormField>
              <FormField label="Light again at" htmlFor={`${id}-to`} variant="info">
                <input id={`${id}-to`} type="time" value={to} onChange={(e) => setTime('to', e.target.value)} className={INPUT}
                  aria-invalid={timeError ? true : undefined} aria-describedby={timeError ? `${id}-time-err` : undefined} />
              </FormField>
              <div id={`${id}-time-err`} className="sm:col-span-2 -mt-[8px]"><FieldError message={timeError} /></div>
            </div>
          )}
          {autoStatus && <p role="status" className="m-0 py-[12px] border-t border-casevo-border-soft text-[12.5px] text-casevo-text-soft">{autoStatus}</p>}
        </div>
      )}
      {saveFailed && (
        <ErrorNote className="mt-[12px]" title="Couldn’t save the theme to your account"
          action={<button type="button" className={LINK} onClick={() => save(appearance)}>Try again</button>}>
          {saveWhy ? `${saveWhy} ` : ''}It’s applied on this device, but your other devices won’t pick it up until it saves.
        </ErrorNote>
      )}
    </div>
  )
}

export default ThemeSettings
